import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import {
  fetchBillsOfCosts, fetchBillOfCosts, createBillOfCosts, deleteBillOfCosts,
  pullWipIntoBill, recalculateBill, submitBillForTaxation, recordBillTaxation,
  issueBillAllocatur, settleBill, detectBillChanges, syncBillItem,
  fetchCourts, fetchTariffSchedules, exportBillOfCostsPdf, listCases,
} from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';
import TariffCodesTab from './TariffCodesTab';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700',
  REVIEW: 'bg-blue-100 text-blue-700',
  SERVED: 'bg-yellow-100 text-yellow-800',
  NEGOTIATION: 'bg-orange-100 text-orange-700',
  SUBMITTED_FOR_TAXATION: 'bg-purple-100 text-purple-700',
  TAXED: 'bg-indigo-100 text-indigo-700',
  ALLOCATUR_ISSUED: 'bg-green-100 text-green-700',
  SETTLED: 'bg-emerald-100 text-emerald-700',
};

function BillOfCostsManagement({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [courts, setCourts] = useState([]);
  const [tariffSchedules, setTariffSchedules] = useState([]);
  const [changedItems, setChangedItems] = useState([]);
  const [cases, setCases] = useState([]);

  // Create form
  const [formData, setFormData] = useState({
    case: '', court: '', tariff_schedule: '', opposing_party: '', notes: '',
    // Order 74 Filing Notice + document fields
    judge_name: '', venue: '', party_represented: '', scale_type: '',
    order_date: '', opposing_firm_address: '', registrar_address: '',
  });

  // Taxation form
  const [showTaxationForm, setShowTaxationForm] = useState(false);
  const [taxationDate, setTaxationDate] = useState('');
  const [taxationItems, setTaxationItems] = useState([]);

  // Allocatur form
  const [showAllocaturForm, setShowAllocaturForm] = useState(false);
  const [allocaturData, setAllocaturData] = useState({ allocatur_amount: '', allocatur_date: '', allocatur_reference: '' });

  // Settlement form
  const [showSettlementForm, setShowSettlementForm] = useState(false);
  const [settlementData, setSettlementData] = useState({ settlement_amount: '', settlement_date: '', settlement_notes: '' });

  // Filing Notice details sub-section toggle
  const [showFilingNoticeDetails, setShowFilingNoticeDetails] = useState(false);

  const canManage = useMemo(
    () => ['PARTNER', 'SUPERUSER', 'ASSOCIATE', 'ACCOUNTANT'].includes(userRole),
    [userRole]
  );

  const canEditTariffs = useMemo(
    () => ['PARTNER', 'SUPERUSER', 'ASSOCIATE', 'PARALEGAL'].includes(userRole),
    [userRole]
  );

  const [activeTab, setActiveTab] = useState('bills');

  // Fetch helpers
  const loadBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchBillsOfCosts(authAxios);
      setBills(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage('error', 'Failed to load bills of costs.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, showFormMessage]);

  const loadLookups = useCallback(async () => {
    try {
      const [c, ts, cs] = await Promise.all([
        fetchCourts(authAxios),
        fetchTariffSchedules(authAxios),
        listCases(authAxios),
      ]);
      setCourts(Array.isArray(c) ? c : []);
      setTariffSchedules(Array.isArray(ts) ? ts : []);
      setCases(Array.isArray(cs) ? cs : []);
    } catch { /* silent */ }
  }, [authAxios]);

  useEffect(() => { loadBills(); loadLookups(); }, [loadBills, loadLookups]);

  const openDetail = useCallback(async (id) => {
    try {
      const bill = await fetchBillOfCosts(authAxios, id);
      setSelectedBill(bill);
      setChangedItems([]);
    } catch {
      showFormMessage('error', 'Failed to load bill details.');
    }
  }, [authAxios, showFormMessage]);

  // CRUD handlers
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const created = await createBillOfCosts(authAxios, {
        case: formData.case ? parseInt(formData.case) : null,
        court: formData.court ? parseInt(formData.court) : null,
        tariff_schedule: formData.tariff_schedule ? parseInt(formData.tariff_schedule) : null,
        opposing_party: formData.opposing_party,
        notes: formData.notes,
        judge_name: formData.judge_name,
        venue: formData.venue,
        party_represented: formData.party_represented || '',
        scale_type: formData.scale_type || '',
        order_date: formData.order_date || null,
        opposing_firm_address: formData.opposing_firm_address,
        registrar_address: formData.registrar_address,
      });
      showFormMessage('success', 'Bill of Costs created.');
      setShowCreateForm(false);
      setFormData({
        case: '', court: '', tariff_schedule: '', opposing_party: '', notes: '',
        judge_name: '', venue: '', party_represented: '', scale_type: '',
        order_date: '', opposing_firm_address: '', registrar_address: '',
      });
      loadBills();
      openDetail(created.id);
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to create bill.');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Delete Bill of Costs', message: 'Delete this Bill of Costs? This cannot be undone.', confirmText: 'Delete', variant: 'danger' }))) return;
    try {
      await deleteBillOfCosts(authAxios, id);
      showFormMessage('success', 'Bill deleted.');
      setSelectedBill(null);
      loadBills();
    } catch {
      showFormMessage('error', 'Failed to delete bill.');
    }
  };

  // Action handlers
  const handlePullWip = async () => {
    try {
      const result = await pullWipIntoBill(authAxios, selectedBill.id);
      showFormMessage('success', `Pulled ${result.total_items_added} WIP entries.`);
      setSelectedBill(result.bill);
      loadBills();
    } catch (err) {
      showFormMessage('error', err.response?.data?.error || 'Failed to pull WIP.');
    }
  };

  const handleRecalculate = async () => {
    try {
      const updated = await recalculateBill(authAxios, selectedBill.id);
      showFormMessage('success', 'Totals recalculated.');
      setSelectedBill(updated);
      loadBills();
    } catch {
      showFormMessage('error', 'Recalculation failed.');
    }
  };

  const handleSubmitTaxation = async () => {
    if (!(await confirm({ title: 'Submit for Taxation', message: 'Submit this bill for taxation? This will freeze the claimed totals.', confirmText: 'Submit', variant: 'danger' }))) return;
    try {
      const updated = await submitBillForTaxation(authAxios, selectedBill.id);
      showFormMessage('success', 'Bill submitted for taxation. Downloading PDF…');
      setSelectedBill(updated);
      loadBills();
      // Auto-download court PDF on successful submission
      try {
        const blob = await exportBillOfCostsPdf(authAxios, updated.id);
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `bill-of-costs-${updated.bill_number || updated.id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        // PDF download is best-effort; status transition already succeeded
        showFormMessage('warning', 'Bill submitted but PDF download failed — use Download PDF to retry.');
      }
    } catch (err) {
      showFormMessage('error', err.response?.data?.error || 'Submission failed.');
    }
  };

  const handleRecordTaxation = async (e) => {
    e.preventDefault();
    try {
      const updated = await recordBillTaxation(authAxios, selectedBill.id, {
        date_taxed: taxationDate,
        items: taxationItems.map((ti) => ({ id: ti.id, taxed_off_amount: parseFloat(ti.taxed_off_amount) || 0 })),
      });
      showFormMessage('success', 'Taxation recorded.');
      setSelectedBill(updated);
      setShowTaxationForm(false);
      loadBills();
    } catch (err) {
      showFormMessage('error', err.response?.data?.error || 'Recording failed.');
    }
  };

  const handleIssueAllocatur = async (e) => {
    e.preventDefault();
    try {
      const updated = await issueBillAllocatur(authAxios, selectedBill.id, allocaturData);
      showFormMessage('success', 'Allocatur issued.');
      setSelectedBill(updated);
      setShowAllocaturForm(false);
      loadBills();
    } catch (err) {
      showFormMessage('error', err.response?.data?.error || 'Failed to issue allocatur.');
    }
  };

  const handleSettle = async (e) => {
    e.preventDefault();
    try {
      const updated = await settleBill(authAxios, selectedBill.id, settlementData);
      showFormMessage('success', 'Bill settled.');
      setSelectedBill(updated);
      setShowSettlementForm(false);
      loadBills();
    } catch (err) {
      showFormMessage('error', err.response?.data?.error || 'Settlement failed.');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const blob = await exportBillOfCostsPdf(authAxios, selectedBill.id);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bill-of-costs-${selectedBill.bill_number || selectedBill.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showFormMessage('success', 'PDF downloaded.');
    } catch {
      showFormMessage('error', 'PDF export failed.');
    }
  };

  const handleDetectChanges = async () => {
    try {
      const result = await detectBillChanges(authAxios, selectedBill.id);
      setChangedItems(result.changed_items || []);
      if (!result.changed_items?.length) {
        showFormMessage('success', 'No WIP changes detected.');
      } else {
        showFormMessage('warning', `${result.changed_items.length} item(s) have source changes.`);
      }
    } catch {
      showFormMessage('error', 'Change detection failed.');
    }
  };

  const handleSyncItem = async (itemId) => {
    try {
      await syncBillItem(authAxios, selectedBill.id, itemId);
      showFormMessage('success', 'Item re-synced.');
      openDetail(selectedBill.id);
    } catch {
      showFormMessage('error', 'Sync failed.');
    }
  };

  const openTaxationForm = () => {
    if (!selectedBill?.items) return;
    setTaxationDate(new Date().toISOString().split('T')[0]);
    setTaxationItems(selectedBill.items.map((i) => ({
      id: i.id,
      description: i.description,
      claimed_amount: i.claimed_amount || i.party_party_amount,
      taxed_off_amount: i.taxed_off_amount || 0,
    })));
    setShowTaxationForm(true);
  };

  // ---- RENDER ----

  // Detail view
  if (selectedBill) {
    const bill = selectedBill;
    const isDraft = ['DRAFT', 'REVIEW'].includes(bill.status);
    const canSubmitTax = ['DRAFT', 'REVIEW', 'SERVED', 'NEGOTIATION'].includes(bill.status);
    const canRecordTax = bill.status === 'SUBMITTED_FOR_TAXATION';
    const canAllocatur = ['TAXED', 'ALLOCATUR_ISSUED'].includes(bill.status);

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={() => { setSelectedBill(null); loadBills(); }} className="text-blue-600 hover:underline text-sm">&larr; Back to list</button>
          <div className="flex gap-2">
            {isDraft && canManage && (
              <>
                <button onClick={handlePullWip} className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700">Pull WIP</button>
                <button onClick={handleRecalculate} className="px-3 py-1 text-sm bg-gray-600 text-white rounded hover:bg-gray-700">Recalculate</button>
              </>
            )}
            {canSubmitTax && canManage && (
              <button onClick={handleSubmitTaxation} className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700">Submit for Taxation</button>
            )}
            {canRecordTax && canManage && (
              <button onClick={openTaxationForm} className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">Record Taxation</button>
            )}
            {canAllocatur && canManage && (
              <button onClick={() => { setAllocaturData({ allocatur_amount: '', allocatur_date: new Date().toISOString().split('T')[0], allocatur_reference: '' }); setShowAllocaturForm(true); }} className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700">Issue Allocatur</button>
            )}
            {bill.status !== 'SETTLED' && canManage && (
              <button onClick={() => { setSettlementData({ settlement_amount: '', settlement_date: new Date().toISOString().split('T')[0], settlement_notes: '' }); setShowSettlementForm(true); }} className="px-3 py-1 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700">Settle</button>
            )}
            <button onClick={handleDetectChanges} className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600">Detect Changes</button>
            <button onClick={handleDownloadPdf} className="px-3 py-1 text-sm bg-gray-700 text-white rounded hover:bg-gray-800">Download PDF</button>
            {isDraft && canManage && (
              <button onClick={() => handleDelete(bill.id)} className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
            )}
          </div>
        </div>

        {/* Bill Header Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-bold text-gray-800">{bill.bill_number || 'New Bill'}</h2>
              <p className="text-gray-500 text-sm mt-1">Case: {bill.case_title || bill.case}</p>
              {bill.court_name && <p className="text-gray-500 text-sm">Court: {bill.court_name}</p>}
              {bill.judge_name && <p className="text-gray-500 text-sm">Before: {bill.judge_name}</p>}
              {bill.venue && <p className="text-gray-500 text-sm">Held at: {bill.venue}</p>}
              {bill.party_represented_display && <p className="text-gray-500 text-sm">Party: {bill.party_represented_display}</p>}
              {bill.scale_type_display && <p className="text-gray-500 text-sm">Scale: {bill.scale_type_display}</p>}
              {bill.order_date && <p className="text-gray-500 text-sm">Order Date: {bill.order_date}</p>}
              {bill.prepared_by_username && <p className="text-gray-500 text-sm">Prepared by: {bill.prepared_by_username}</p>}
              {bill.opposing_party && <p className="text-gray-500 text-sm">Opposing: {bill.opposing_party}</p>}
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[bill.status] || 'bg-gray-100'}`}>
              {bill.status_display || bill.status}
            </span>
          </div>

          {/* Totals Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-500 uppercase">Subtotal A&C</p>
              <p className="text-lg font-semibold">{formatCurrency(bill.subtotal_ac)}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-xs text-gray-500 uppercase">Subtotal P&P</p>
              <p className="text-lg font-semibold">{formatCurrency(bill.subtotal_pp)}</p>
            </div>
            <div className="bg-blue-50 p-3 rounded">
              <p className="text-xs text-blue-500 uppercase">Total A&C</p>
              <p className="text-lg font-bold text-blue-700">{formatCurrency(bill.total_ac)}</p>
            </div>
            <div className="bg-blue-50 p-3 rounded">
              <p className="text-xs text-blue-500 uppercase">Total P&P</p>
              <p className="text-lg font-bold text-blue-700">{formatCurrency(bill.total_pp)}</p>
            </div>
            {bill.drawing_fee_ac > 0 && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500 uppercase">Drawing Fee A&C</p>
                <p className="text-sm font-medium">{formatCurrency(bill.drawing_fee_ac)}</p>
              </div>
            )}
            {bill.drawing_fee_pp > 0 && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-500 uppercase">Drawing Fee P&P</p>
                <p className="text-sm font-medium">{formatCurrency(bill.drawing_fee_pp)}</p>
              </div>
            )}
            {bill.claimed_total && (
              <div className="bg-purple-50 p-3 rounded">
                <p className="text-xs text-purple-500 uppercase">Claimed Total</p>
                <p className="text-lg font-bold text-purple-700">{formatCurrency(bill.claimed_total)}</p>
              </div>
            )}
            {bill.taxed_total && (
              <div className="bg-indigo-50 p-3 rounded">
                <p className="text-xs text-indigo-500 uppercase">Taxed Total</p>
                <p className="text-lg font-bold text-indigo-700">{formatCurrency(bill.taxed_total)}</p>
              </div>
            )}
            {bill.allocatur_amount && (
              <div className="bg-green-50 p-3 rounded">
                <p className="text-xs text-green-500 uppercase">Allocatur</p>
                <p className="text-lg font-bold text-green-700">{formatCurrency(bill.allocatur_amount)}</p>
              </div>
            )}
            {bill.settlement_amount && (
              <div className="bg-emerald-50 p-3 rounded">
                <p className="text-xs text-emerald-500 uppercase">Settlement</p>
                <p className="text-lg font-bold text-emerald-700">{formatCurrency(bill.settlement_amount)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Line Items Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="font-semibold text-gray-700">Line Items ({bill.items?.length || 0})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">#</th>
                  <th className="px-4 py-2 text-left">Description</th>
                  <th className="px-4 py-2 text-center">Section</th>
                  <th className="px-4 py-2 text-center">Item</th>
                  <th className="px-4 py-2 text-center">Type</th>
                  <th className="px-4 py-2 text-left">Tariff</th>
                  <th className="px-4 py-2 text-left">Staff</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">A&amp;C</th>
                  <th className="px-4 py-2 text-right">P&amp;P</th>
                  <th className="px-4 py-2 text-right">Shortfall</th>
                  {canRecordTax && <th className="px-4 py-2 text-right">Claimed</th>}
                  {bill.status === 'TAXED' && <th className="px-4 py-2 text-right">Allowed</th>}
                  <th className="px-4 py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(bill.items || []).map((item) => {
                  const hasChange = changedItems.some((c) => c.item_id === item.id);
                  return (
                    <tr key={item.id} className={hasChange ? 'bg-yellow-50' : ''}>
                      <td className="px-4 py-2 text-gray-500">{item.item_number}</td>
                      <td className="px-4 py-2 max-w-xs truncate" title={item.description}>{item.description}</td>
                      <td className="px-4 py-2 text-center text-gray-500 text-xs">{item.section || '—'}</td>
                      <td className="px-4 py-2 text-center text-gray-500 text-xs">{item.item_ref || '—'}</td>
                      <td className="px-4 py-2 text-center">
                        {item.is_disbursement
                          ? <span className="text-xs bg-orange-100 text-orange-700 px-1 rounded">Disb</span>
                          : <span className="text-xs bg-blue-50 text-blue-600 px-1 rounded">Fee</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-500">{item.tariff_code || '—'}</td>
                      <td className="px-4 py-2 text-gray-500">{item.staff_name || item.staff_member_name || '—'}</td>
                      <td className="px-4 py-2 text-right">{item.quantity}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(item.attorney_client_amount)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(item.party_party_amount)}</td>
                      <td className="px-4 py-2 text-right text-orange-600">{formatCurrency(item.shortfall)}</td>
                      {canRecordTax && <td className="px-4 py-2 text-right">{formatCurrency(item.claimed_amount)}</td>}
                      {bill.status === 'TAXED' && <td className="px-4 py-2 text-right font-medium text-green-700">{formatCurrency(item.allowed_amount)}</td>}
                      <td className="px-4 py-2 text-center">
                        {hasChange ? (
                          <button onClick={() => handleSyncItem(item.id)} className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded hover:bg-yellow-200">
                            Changed — Sync
                          </button>
                        ) : (
                          <span className="text-xs text-green-600">OK</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(!bill.items || bill.items.length === 0) && (
                  <tr><td colSpan={11} className="px-4 py-6 text-center text-gray-400">No line items yet. Use "Pull WIP" to import time & expense entries.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Taxation Form Modal */}
        {showTaxationForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Record Taxation Outcome</h3>
              <form onSubmit={handleRecordTaxation}>
                <label className="block mb-3">
                  <span className="text-sm text-gray-600">Date Taxed</span>
                  <input type="date" value={taxationDate} onChange={(e) => setTaxationDate(e.target.value)} className="mt-1 block w-full border rounded px-3 py-2" required />
                </label>
                <table className="min-w-full text-sm mt-2">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right">Claimed</th>
                      <th className="px-3 py-2 text-right">Taxed Off</th>
                      <th className="px-3 py-2 text-right">Allowed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {taxationItems.map((ti, idx) => (
                      <tr key={ti.id}>
                        <td className="px-3 py-2 max-w-xs truncate">{ti.description}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(ti.claimed_amount)}</td>
                        <td className="px-3 py-2 text-right">
                          <input type="number" step="0.01" min="0" value={ti.taxed_off_amount}
                            onChange={(e) => {
                              const updated = [...taxationItems];
                              updated[idx] = { ...updated[idx], taxed_off_amount: e.target.value };
                              setTaxationItems(updated);
                            }}
                            className="w-24 border rounded px-2 py-1 text-right" />
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-green-700">
                          {formatCurrency((parseFloat(ti.claimed_amount) || 0) - (parseFloat(ti.taxed_off_amount) || 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end gap-3 mt-4">
                  <button type="button" onClick={() => setShowTaxationForm(false)} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Save Taxation</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Allocatur Form Modal */}
        {showAllocaturForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Issue Allocatur</h3>
              <form onSubmit={handleIssueAllocatur}>
                <label className="block mb-3">
                  <span className="text-sm text-gray-600">Allocatur Amount (P)</span>
                  <input type="number" step="0.01" min="0" value={allocaturData.allocatur_amount}
                    onChange={(e) => setAllocaturData({ ...allocaturData, allocatur_amount: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2" required />
                </label>
                <label className="block mb-3">
                  <span className="text-sm text-gray-600">Date</span>
                  <input type="date" value={allocaturData.allocatur_date}
                    onChange={(e) => setAllocaturData({ ...allocaturData, allocatur_date: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2" required />
                </label>
                <label className="block mb-3">
                  <span className="text-sm text-gray-600">Reference</span>
                  <input type="text" value={allocaturData.allocatur_reference}
                    onChange={(e) => setAllocaturData({ ...allocaturData, allocatur_reference: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2" />
                </label>
                <div className="flex justify-end gap-3 mt-4">
                  <button type="button" onClick={() => setShowAllocaturForm(false)} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">Issue</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Settlement Form Modal */}
        {showSettlementForm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Record Settlement</h3>
              <form onSubmit={handleSettle}>
                <label className="block mb-3">
                  <span className="text-sm text-gray-600">Settlement Amount (P)</span>
                  <input type="number" step="0.01" min="0" value={settlementData.settlement_amount}
                    onChange={(e) => setSettlementData({ ...settlementData, settlement_amount: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2" required />
                </label>
                <label className="block mb-3">
                  <span className="text-sm text-gray-600">Date</span>
                  <input type="date" value={settlementData.settlement_date}
                    onChange={(e) => setSettlementData({ ...settlementData, settlement_date: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2" required />
                </label>
                <label className="block mb-3">
                  <span className="text-sm text-gray-600">Notes</span>
                  <textarea value={settlementData.settlement_notes}
                    onChange={(e) => setSettlementData({ ...settlementData, settlement_notes: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2" rows="3" />
                </label>
                <div className="flex justify-end gap-3 mt-4">
                  <button type="button" onClick={() => setShowSettlementForm(false)} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700">Settle</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }
  // ---- LIST VIEW ----
  return (
    <div className="space-y-6">
      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('bills')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'bills'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Bills of Costs
          </button>
          <button
            onClick={() => setActiveTab('tariff-codes')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'tariff-codes'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Tariff Codes
          </button>
        </nav>
      </div>

      {/* Tariff Codes tab */}
      {activeTab === 'tariff-codes' && (
        <TariffCodesTab authAxios={authAxios} canEdit={canEditTariffs} showFormMessage={showFormMessage} />
      )}

      {/* Bills tab content */}
      {activeTab === 'bills' && (<>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Bills of Costs</h1>
        {canManage && (
          <button onClick={() => setShowCreateForm(!showCreateForm)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
            {showCreateForm ? 'Cancel' : '+ New Bill'}
          </button>
        )}
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-gray-600 block mb-1">Case *</span>
              <Select
                options={cases.map((c) => ({ value: c.id, label: `${c.case_number || c.id} — ${c.title}` }))}
                value={formData.case ? (() => { const c = cases.find(x => x.id === parseInt(formData.case)); return c ? { value: c.id, label: `${c.case_number || c.id} — ${c.title}` } : { value: formData.case, label: String(formData.case) }; })() : null}
                onChange={(selected) => setFormData({ ...formData, case: selected ? selected.value : '' })}
                isClearable
                isSearchable
                placeholder="Search cases..."
                className="text-sm"
              />
            </div>
            <label className="block">
              <span className="text-sm text-gray-600">Court</span>
              <select value={formData.court} onChange={(e) => setFormData({ ...formData, court: e.target.value })}
                className="mt-1 block w-full border rounded px-3 py-2">
                <option value="">— Select Court —</option>
                {courts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-gray-600">Tariff Schedule</span>
              <select value={formData.tariff_schedule} onChange={(e) => setFormData({ ...formData, tariff_schedule: e.target.value })}
                className="mt-1 block w-full border rounded px-3 py-2">
                <option value="">— Select Schedule —</option>
                {tariffSchedules.map((ts) => <option key={ts.id} value={ts.id}>{ts.name} ({ts.court_type_display})</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-gray-600">Scale</span>
              <select value={formData.scale_type} onChange={(e) => setFormData({ ...formData, scale_type: e.target.value })}
                className="mt-1 block w-full border rounded px-3 py-2">
                <option value="">— Select Scale —</option>
                <option value="PARTY_PARTY">Party and Party</option>
                <option value="ATTORNEY_CLIENT">Attorney and Client</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-gray-600">Opposing Party</span>
              <input type="text" value={formData.opposing_party} onChange={(e) => setFormData({ ...formData, opposing_party: e.target.value })}
                className="mt-1 block w-full border rounded px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-sm text-gray-600">Party Represented</span>
              <select value={formData.party_represented} onChange={(e) => setFormData({ ...formData, party_represented: e.target.value })}
                className="mt-1 block w-full border rounded px-3 py-2">
                <option value="">— Select —</option>
                <option value="PLAINTIFF">Plaintiff</option>
                <option value="DEFENDANT">Defendant</option>
                <option value="APPLICANT">Applicant</option>
                <option value="RESPONDENT">Respondent</option>
              </select>
            </label>
          </div>

          {/* Filing Notice Details (collapsible) */}
          <div className="border rounded p-3 bg-gray-50">
            <button type="button" onClick={() => setShowFilingNoticeDetails(!showFilingNoticeDetails)}
              className="text-sm font-medium text-blue-700 flex items-center gap-1">
              {showFilingNoticeDetails ? '▾' : '▸'} Filing Notice Details (judge, venue, order date, addresses)
            </button>
            {showFilingNoticeDetails && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                <label className="block">
                  <span className="text-sm text-gray-600">Judge Name</span>
                  <input type="text" value={formData.judge_name} onChange={(e) => setFormData({ ...formData, judge_name: e.target.value })}
                    placeholder="e.g. GAREKWE J." className="mt-1 block w-full border rounded px-3 py-2" />
                </label>
                <label className="block">
                  <span className="text-sm text-gray-600">Venue (city held at)</span>
                  <input type="text" value={formData.venue} onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    placeholder="e.g. GABORONE" className="mt-1 block w-full border rounded px-3 py-2" />
                </label>
                <label className="block">
                  <span className="text-sm text-gray-600">Order Date</span>
                  <input type="date" value={formData.order_date} onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2" />
                </label>
                <div />
                <label className="block md:col-span-2">
                  <span className="text-sm text-gray-600">Registrar Address (for Filing Notice)</span>
                  <textarea value={formData.registrar_address} onChange={(e) => setFormData({ ...formData, registrar_address: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2" rows="3"
                    placeholder="HIGH COURT OF BOTSWANA&#10;GABORONE" />
                </label>
                <label className="block md:col-span-2">
                  <span className="text-sm text-gray-600">Opposing Attorneys Address (for Filing Notice)</span>
                  <textarea value={formData.opposing_firm_address} onChange={(e) => setFormData({ ...formData, opposing_firm_address: e.target.value })}
                    className="mt-1 block w-full border rounded px-3 py-2" rows="3" />
                </label>
              </div>
            )}
          </div>
          <label className="block">
            <span className="text-sm text-gray-600">Notes</span>
            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="mt-1 block w-full border rounded px-3 py-2" rows="2" />
          </label>
          <div className="flex justify-end">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Create Bill</button>
          </div>
        </form>
      )}

      {/* Bills List */}
      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Loading...</div>
      ) : bills.length === 0 ? (
        <div className="text-center text-gray-400 py-12">No bills of costs found.</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Bill #</th>
                <th className="px-4 py-3 text-left">Case</th>
                <th className="px-4 py-3 text-left">Court</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-right">Total A&C</th>
                <th className="px-4 py-3 text-right">Total P&P</th>
                <th className="px-4 py-3 text-right">Items</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bills.map((bill) => (
                <tr key={bill.id} onClick={() => openDetail(bill.id)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-3 font-medium text-blue-600">{bill.bill_number}</td>
                  <td className="px-4 py-3">{bill.case_title || bill.case}</td>
                  <td className="px-4 py-3 text-gray-500">{bill.court_name || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[bill.status] || 'bg-gray-100'}`}>
                      {bill.status_display || bill.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{bill.date_prepared}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(bill.total_ac)}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(bill.total_pp)}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{bill.item_count ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>)}
    </div>
  );
}

export default BillOfCostsManagement;
